import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import {
  Observable,
  Subject,
  takeUntil,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { FormBase } from '../../../_classes/form-base.class';
import { IBusinessCategory } from '../../../_interfaces/IBusinessCategory';
import ICurrency from '../../../_interfaces/ICurrency';
import ICustomerCreateWizardPayload from '../../../_interfaces/ICustomerCreateWizardPayload';
import { EResources } from '../../../_resources/EResources';
import { BusinessCategoriesService } from '../../../_services/business-categories/business-categories.service';
import { CurrenciesService } from '../../../_services/currencies/currencies.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import { SessionService } from '../../../_services/session/session.service';

type IdentifierStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

interface InitialModuleCard {
  modules_id: number;
  identifier: string;
  name: string;
  monthly_price: number;
  enabled: boolean;
}

interface LanguageOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-form-customer-sign-up',
  templateUrl: './form-customer-sign-up.component.html',
  styleUrl: './form-customer-sign-up.component.scss',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
  ],
})
export class FormCustomerSignUpComponent
  extends FormBase<ICustomerCreateWizardPayload>
  implements OnInit, OnDestroy
{
  public currencies: ICurrency[] = [];
  public businessCategories: IBusinessCategory[] = [];
  public submitted = false;
  public submitting = false;
  public currentStep = 1;
  public readonly totalSteps = 5;
  public wizardVisible = false;
  public moduleCards: InitialModuleCard[] = [];
  public selectedModuleIdentifiers: string[] = [];

  public identifierStatus: IdentifierStatus = 'idle';
  public identifierSuggestion: string | null = null;
  public readonly languageOptions: LanguageOption[] = [
    { label: 'English', value: 'en' },
    { label: 'Español', value: 'es' },
    { label: 'Català', value: 'ca' },
  ];

  public form: ReturnType<FormBuilder['group']>;

  private readonly destroy$ = new Subject<void>();
  private readonly nameChanges$ = new Subject<string>();
  private readonly identifierChanges$ = new Subject<string>();
  private suppressIdentifierChange = false;
  private identifierManualOverride = false;
  private readonly coreModules = new Set(['appointments', 'patients']);

  constructor(
    public readonly session: SessionService,
    public readonly customersService: CustomersService,
    public readonly translate: TranslateService,
    public readonly messageService: MessageService,
    private readonly currenciesService: CurrenciesService,
    private readonly businessCategoriesService: BusinessCategoriesService,
    private readonly fb: FormBuilder,
  ) {
    super(session, messageService, translate);
    this.form = this.fb.group({
      customer: this.fb.group({
        name: ['', [Validators.required]],
        identifier: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(40),
            Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
          ],
        ],
        currency: ['EUR', [Validators.required]],
        business_category_ids: [[], [Validators.required]],
      }),
      contact: this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        phone: ['', [Validators.required, Validators.maxLength(50)]],
        contact_name: [''],
        postal_address: ['', [Validators.maxLength(200)]],
      }),
      billing: this.fb.group({
        legal_name: ['', [Validators.required, Validators.maxLength(150)]],
        fiscal_id: ['', [Validators.required, Validators.maxLength(30)]],
        address_line1: ['', [Validators.required, Validators.maxLength(200)]],
        address_line2: ['', [Validators.maxLength(200)]],
        city: ['', [Validators.required, Validators.maxLength(100)]],
        province: ['', [Validators.required, Validators.maxLength(100)]],
        postal_code: ['', [Validators.required, Validators.maxLength(20)]],
        country_code: [
          'ES',
          [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(5),
          ],
        ],
        billing_notes: [''],
      }),
      admin: this.fb.group({
        name: ['', [Validators.required]],
        surname: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],
        language: [this.session.normalizeLanguage(this.translate.currentLang), [Validators.required]],
      }),
      services: this.fb.group({
        initial_site_name: [
          '',
          [Validators.required, Validators.maxLength(120)],
        ],
        staff_users: [1, [Validators.required, Validators.min(1)]],
        sites: [1, [Validators.required, Validators.min(1)]],
      }),
    });
  }

  get customerGroup(): any {
    return this.form.get('customer');
  }

  get contactGroup(): any {
    return this.form.get('contact');
  }

  get billingGroup(): any {
    return this.form.get('billing');
  }

  get adminGroup(): any {
    return this.form.get('admin');
  }

  get servicesGroup(): any {
    return this.form.get('services');
  }

  get progressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.currenciesService.getAll().subscribe((currencies) => {
      this.currencies = currencies || [];
      if (!this.customerGroup?.controls?.['currency']?.value) {
        const fallback =
          this.currencies.find(
            (currency) => `${currency.code || ''}`.toUpperCase() === 'EUR',
          ) || this.currencies[0];
        if (fallback?.code) {
          this.customerGroup?.controls?.['currency']?.setValue(fallback.code);
        }
      }
    });

    this.businessCategoriesService.getAll().subscribe((categories) => {
      this.businessCategories = (categories || [])
        .filter((category) => !!category.active)
        .map((category) => ({
          ...category,
          displayName: this.getTranslatedCategoryName(category),
        }));
    });

    this.customersService.getGlobalPricingDefaults().subscribe({
      next: (response) => {
        this.moduleCards = (response?.items || [])
          .filter(
            (item: any) => item?.concept_type === 'MODULE' && item?.module,
          )
          .map((item: any) => ({
            modules_id: Number(item.module.id || 0),
            identifier: `${item.module.identifier || ''}`.trim().toLowerCase(),
            name: `${item.module.name || ''}`,
            monthly_price: Number(item.monthly_price || 0),
            enabled: false,
          }))
          .filter(
            (item) =>
              !!item.identifier && !this.coreModules.has(item.identifier),
          );
      },
      error: () => {
        this.moduleCards = [];
      },
    });

    this.nameChanges$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.requestSuggestedIdentifier(value || '', false);
      });

    this.identifierChanges$
      .pipe(debounceTime(280), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.validateManualIdentifier(value || '');
      });

    this.customerGroup?.controls?.['name']?.valueChanges
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const currentSiteName =
          `${this.servicesGroup?.controls?.['initial_site_name']?.value || ''}`.trim();
        const siteControl = this.servicesGroup?.controls?.['initial_site_name'];
        if (!siteControl?.dirty && !currentSiteName) {
          siteControl?.setValue(`${value || ''}`.trim());
        }
        if (this.identifierManualOverride) return;
        this.nameChanges$.next(`${value || ''}`);
      });

    this.customerGroup?.controls?.['identifier']?.valueChanges
      ?.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.suppressIdentifierChange) return;
        this.identifierManualOverride = true;
        this.identifierChanges$.next(`${value || ''}`);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openWizard(): void {
    this.clear();
    this.wizardVisible = true;
  }

  closeWizard(): void {
    if (this.submitting) return;
    this.wizardVisible = false;
    this.clear();
  }

  useSuggestedIdentifier(): void {
    if (!this.identifierSuggestion) return;
    this.suppressIdentifierChange = true;
    this.customerGroup?.controls?.['identifier']?.setValue(
      this.identifierSuggestion,
    );
    this.customerGroup?.controls?.['identifier']?.markAsDirty();
    this.suppressIdentifierChange = false;
    this.identifierSuggestion = null;
    this.identifierStatus = 'available';
  }

  nextStep(): void {
    if (!this.isCurrentStepValid()) {
      this.markCurrentStepTouched();
      return;
    }
    this.currentStep = Math.min(this.totalSteps, this.currentStep + 1);
  }

  previousStep(): void {
    this.currentStep = Math.max(1, this.currentStep - 1);
  }

  toggleModule(identifier: string): void {
    const normalized = `${identifier || ''}`.trim().toLowerCase();
    if (!normalized) return;

    if (this.selectedModuleIdentifiers.includes(normalized)) {
      this.selectedModuleIdentifiers = this.selectedModuleIdentifiers.filter(
        (item) => item !== normalized,
      );
    } else {
      this.selectedModuleIdentifiers = [
        ...this.selectedModuleIdentifiers,
        normalized,
      ];
    }
  }

  isModuleSelected(identifier: string): boolean {
    const normalized = `${identifier || ''}`.trim().toLowerCase();
    return this.selectedModuleIdentifiers.includes(normalized);
  }

  async execute(): Promise<void> {
    this.submitted = true;
    this.clearErrors();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.currentStep = this.resolveFirstInvalidStep();
      return;
    }

    const raw = this.form.getRawValue();
    this.payload = {
      customer: {
        name: `${raw.customer?.name || ''}`.trim(),
        identifier: `${raw.customer?.identifier || ''}`.trim().toLowerCase(),
        currency: `${raw.customer?.currency || 'EUR'}`.trim().toUpperCase(),
        business_category_ids: raw.customer?.business_category_ids || [],
        email: `${raw.contact?.email || ''}`.trim().toLowerCase(),
        phone: `${raw.contact?.phone || ''}`.trim(),
        contact_name: `${raw.contact?.contact_name || ''}`.trim() || undefined,
        legal_name: `${raw.billing?.legal_name || ''}`.trim(),
        fiscal_id: `${raw.billing?.fiscal_id || ''}`.trim(),
        address_line1: `${raw.billing?.address_line1 || ''}`.trim(),
        address_line2:
          `${raw.billing?.address_line2 || ''}`.trim() || undefined,
        city: `${raw.billing?.city || ''}`.trim(),
        province: `${raw.billing?.province || ''}`.trim(),
        postal_code: `${raw.billing?.postal_code || ''}`.trim(),
        country_code: `${raw.billing?.country_code || ''}`.trim().toUpperCase(),
        billing_email: `${raw.contact?.email || ''}`.trim().toLowerCase(),
        billing_notes:
          `${raw.billing?.billing_notes || ''}`.trim() || undefined,
      },
      user: {
        name: `${raw.admin?.name || ''}`.trim(),
        surname: `${raw.admin?.surname || ''}`.trim(),
        email: `${raw.admin?.email || ''}`.trim().toLowerCase(),
        language: this.session.normalizeLanguage(raw.admin?.language),
      },
      initial_site_name: `${raw.services?.initial_site_name || ''}`.trim(),
      initial_subscription: {
        staff_users: Math.max(1, Number(raw.services?.staff_users || 1)),
        sites: Math.max(1, Number(raw.services?.sites || 1)),
        module_identifiers: [...this.selectedModuleIdentifiers],
      },
    };

    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }

  clear(): void {
    this.payload = {
      customer: {
        name: '',
        identifier: '',
        email: '',
        phone: '',
        currency: 'EUR',
        business_category_ids: [],
        legal_name: '',
        fiscal_id: '',
        address_line1: '',
        city: '',
        province: '',
        postal_code: '',
        country_code: 'ES',
        billing_email: '',
      },
      user: {
        name: '',
        surname: '',
        email: '',
        language: this.session.normalizeLanguage(this.translate.currentLang),
      },
      initial_site_name: '',
      initial_subscription: {
        staff_users: 1,
        sites: 1,
        module_identifiers: [],
      },
    };

    this.form.reset({
      customer: {
        name: '',
        identifier: '',
        currency: 'EUR',
        business_category_ids: [],
      },
      contact: {
        email: '',
        phone: '',
        contact_name: '',
        postal_address: '',
      },
      billing: {
        legal_name: '',
        fiscal_id: '',
        address_line1: '',
        address_line2: '',
        city: '',
        province: '',
        postal_code: '',
        country_code: 'ES',
        billing_notes: '',
      },
      admin: {
        name: '',
        surname: '',
        email: '',
        language: this.session.normalizeLanguage(this.translate.currentLang),
      },
      services: {
        initial_site_name: '',
        staff_users: 1,
        sites: 1,
      },
    });

    this.identifierManualOverride = false;
    this.identifierStatus = 'idle';
    this.identifierSuggestion = null;
    this.selectedModuleIdentifiers = [];
    this.currentStep = 1;
    this.submitted = false;
  }

  public clearErrors(): void {
    this.errors = {
      customer: {
        name: '',
        identifier: '',
      },
      contact: {
        email: '',
        phone: '',
      },
      billing: {
        legal_name: '',
      },
      admin: {
        name: '',
        surname: '',
        email: '',
      },
      services: {
        initial_site_name: '',
      },
    };
  }

  generatePayload(): ICustomerCreateWizardPayload {
    return this.payload;
  }

  getResourceName(): string {
    return EResources.CUSTOMERS;
  }

  updateFn(payload: ICustomerCreateWizardPayload): Observable<any> {
    throw new Error('Method not implemented.');
  }

  createFn(payload: ICustomerCreateWizardPayload): Observable<any> {
    return this.customersService.create(payload);
  }

  override createMessageSuccess() {
    this.wizardVisible = false;
    super.createMessageSuccess();
  }

  properties(): string[] {
    return ['customer', 'contact', 'billing', 'admin', 'services'];
  }

  async validate(): Promise<void> {
    if (this.form.invalid) {
      throw this.errors;
    }
  }

  stepKey(step: number): string {
    switch (step) {
      case 1:
        return 'ADMIN.CUSTOMERS.WIZARD.STEP_1';
      case 2:
        return 'ADMIN.CUSTOMERS.WIZARD.STEP_2';
      case 3:
        return 'ADMIN.CUSTOMERS.WIZARD.STEP_3';
      case 4:
        return 'ADMIN.CUSTOMERS.WIZARD.STEP_4';
      case 5:
        return 'ADMIN.CUSTOMERS.WIZARD.STEP_5';
      default:
        return '';
    }
  }

  getIdentifierStatusKey(): string {
    switch (this.identifierStatus) {
      case 'checking':
        return 'ADMIN.CUSTOMERS.WIZARD.IDENTIFIER_CHECKING';
      case 'available':
        return 'ADMIN.CUSTOMERS.WIZARD.IDENTIFIER_AVAILABLE';
      case 'taken':
        return 'ADMIN.CUSTOMERS.WIZARD.IDENTIFIER_TAKEN';
      case 'invalid':
        return 'ADMIN.CUSTOMERS.WIZARD.IDENTIFIER_INVALID';
      default:
        return '';
    }
  }

  getModuleLabel(identifier: string, fallback: string): string {
    const key = `ADMIN.CUSTOMERS.MODULE_LABELS.${`${identifier || ''}`.trim().toUpperCase()}`;
    const translated = `${this.translate.instant(key) || ''}`.trim();
    if (!translated || translated === key) {
      return fallback || identifier;
    }
    return translated;
  }

  private getTranslatedCategoryName(category: IBusinessCategory): string {
    const code = `${category?.code || ''}`.trim();
    const fallback = `${category?.name || ''}`.trim();
    if (!code) {
      return fallback;
    }

    const key = `BUSINESS_CATEGORIES.${code}`;
    const translated = `${this.translate.instant(key) || ''}`.trim();
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  }

  private requestSuggestedIdentifier(
    name: string,
    keepManualValue: boolean,
  ): void {
    if (!`${name || ''}`.trim()) {
      return;
    }

    this.identifierStatus = 'checking';
    this.customersService.suggestCustomerIdentifier(name).subscribe({
      next: (result) => {
        const suggested = `${result?.suggested_identifier || ''}`
          .trim()
          .toLowerCase();
        if (!suggested) {
          this.identifierStatus = 'invalid';
          return;
        }

        if (!keepManualValue) {
          this.suppressIdentifierChange = true;
          this.customerGroup?.controls?.['identifier']?.setValue(suggested);
          this.suppressIdentifierChange = false;
          this.identifierManualOverride = false;
        }

        this.identifierSuggestion = null;
        this.identifierStatus = 'available';
      },
      error: () => {
        this.identifierStatus = 'invalid';
      },
    });
  }

  private validateManualIdentifier(identifier: string): void {
    const control = this.customerGroup?.controls?.['identifier'];
    if (!control) return;
    if (control.invalid) {
      this.identifierStatus = 'invalid';
      this.identifierSuggestion = null;
      return;
    }

    this.identifierStatus = 'checking';
    this.customersService
      .suggestCustomerIdentifier(undefined, identifier)
      .subscribe({
        next: (result) => {
          const normalizedCandidate = `${result?.normalized_candidate || ''}`
            .trim()
            .toLowerCase();
          const current = `${identifier || ''}`.trim().toLowerCase();
          if (!result?.available || normalizedCandidate !== current) {
            this.identifierStatus = 'taken';
            this.identifierSuggestion =
              `${result?.suggested_identifier || ''}`.trim().toLowerCase() ||
              null;
            return;
          }

          this.identifierSuggestion = null;
          this.identifierStatus = 'available';
        },
        error: () => {
          this.identifierStatus = 'invalid';
          this.identifierSuggestion = null;
        },
      });
  }

  private isCurrentStepValid(): boolean {
    return this.groupForStep(this.currentStep)?.valid === true;
  }

  private markCurrentStepTouched(): void {
    this.groupForStep(this.currentStep)?.markAllAsTouched();
  }

  private resolveFirstInvalidStep(): number {
    for (let step = 1; step <= this.totalSteps; step += 1) {
      if (this.groupForStep(step)?.invalid) {
        return step;
      }
    }
    return 1;
  }

  private groupForStep(step: number) {
    switch (step) {
      case 1:
        return this.customerGroup;
      case 2:
        return this.contactGroup;
      case 3:
        return this.billingGroup;
      case 4:
        return this.adminGroup;
      case 5:
        return this.servicesGroup;
      default:
        return this.customerGroup;
    }
  }
}
